import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, ShieldCheck, BookOpen, Heart } from "lucide-react";
import { MURABBI_APPLICATION_FORM_URL, APPLICATION_LINK_PROPS } from "../config/applicationForm";

/**
 * /apply — a branded landing page that previews the Murabbi Youth Initiative
 * and embeds the Google Form so the shareable URL stays on-domain.
 *
 * The embedded form URL is derived from the same single source of truth
 * (MURABBI_APPLICATION_FORM_URL) so swapping forms requires editing only
 * /src/config/applicationForm.js.
 */

// Google Forms accepts the same /viewform URL inside an iframe.
// Strip the editor query so the embedded view is the clean public form.
const EMBED_URL = MURABBI_APPLICATION_FORM_URL.split("?")[0] + "?embedded=true";

const PILLARS = [
  { Icon: ShieldCheck, label: "Faith" },
  { Icon: Heart, label: "Character" },
  { Icon: BookOpen, label: "Leadership" },
];

const Apply = () => (
  <main data-testid="apply-page" className="bg-ink min-h-screen">
    {/* Ambient header */}
    <section className="relative overflow-hidden border-b border-white/5">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 55% at 75% 10%, rgba(74,143,229,0.12), transparent 65%), radial-gradient(ellipse 45% 50% at 10% 95%, rgba(201,169,97,0.06), transparent 70%)",
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 pt-28 lg:pt-32 pb-12 lg:pb-14">
        <div className="inline-flex items-center gap-3 text-[10.5px] tracking-[0.34em] uppercase text-gold">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold/60 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
          </span>
          First Cohort — Applications Now Open
        </div>

        <h1 className="mt-6 font-serif text-white text-[34px] sm:text-[46px] lg:text-[58px] leading-[1.02] tracking-[-0.015em]">
          Apply to the{" "}
          <span className="italic text-azure-soft">Murabbi Youth Initiative</span>.
        </h1>

        <div className="mt-7 h-px w-20 bg-gold" />

        <p className="mt-7 text-white/75 text-[16px] lg:text-[17px] leading-[1.85] max-w-2xl">
          A mentorship program for middle-school boys that cultivates faith,
          character, and leadership through meaningful relationships, reflective
          learning, and purposeful experiences. Complete the Parent Intake Form
          below to begin the application process.
        </p>

        {/* Pillars row */}
        <div className="mt-9 flex flex-wrap gap-x-8 gap-y-4">
          {PILLARS.map(({ Icon, label }) => (
            <div key={label} className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full border border-azure-soft/40 inline-flex items-center justify-center text-azure-soft">
                <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
              </span>
              <span className="text-[10.5px] tracking-[0.34em] uppercase text-white/70">
                {label}
              </span>
            </div>
          ))}
        </div>

        <div className="mt-9 flex flex-col sm:flex-row gap-3">
          <a
            href="#form"
            data-testid="apply-jump-to-form"
            className="inline-flex items-center justify-center gap-3 bg-azure hover:bg-[#5C9BEC] text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all duration-300"
          >
            Begin Your Application
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.8} />
          </a>
          <Link
            to="/consulting/youth-mentorship"
            data-testid="apply-learn-more"
            className="inline-flex items-center justify-center border border-white/40 hover:border-gold hover:text-gold text-white px-7 py-3.5 text-[11px] tracking-[0.32em] uppercase font-medium transition-all duration-300"
          >
            Learn About the Program
          </Link>
        </div>
      </div>
    </section>

    {/* Embedded form */}
    <section id="form" className="relative">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-14 lg:py-18">
        <div className="mb-8 flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="text-[10.5px] tracking-[0.34em] uppercase text-gold">
              Parent Intake Form
            </div>
            <h2 className="mt-4 font-serif text-white text-[24px] sm:text-[30px] lg:text-[34px] leading-[1.12] tracking-[-0.012em]">
              Complete the form below.
            </h2>
          </div>
          <a
            {...APPLICATION_LINK_PROPS}
            data-testid="apply-open-in-new-tab"
            className="inline-flex items-center gap-2 text-[10.5px] tracking-[0.32em] uppercase text-azure-soft hover:text-white transition-colors"
          >
            Open in a new tab
            <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.8} />
          </a>
        </div>

        <div className="relative border border-white/10 bg-white overflow-hidden">
          <iframe
            src={EMBED_URL}
            title="Murabbi Youth Initiative — Parent Intake Form"
            data-testid="apply-form-iframe"
            className="block w-full"
            style={{ height: "1600px", border: "0" }}
            loading="lazy"
          >
            Loading…
          </iframe>
        </div>

        <p className="mt-6 text-white/55 text-[13px] leading-[1.8] max-w-2xl">
          Having trouble with the embedded form? You can also{" "}
          <a
            {...APPLICATION_LINK_PROPS}
            data-testid="apply-fallback-link"
            className="text-azure-soft hover:text-white underline underline-offset-4"
          >
            open the form directly in Google Forms
          </a>
          . Your responses are submitted securely to Google on submission.
        </p>
      </div>
    </section>

    {/* Closing line */}
    <section className="border-t border-white/5">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-12 text-center">
        <div className="mx-auto h-px w-10 bg-gold/70" />
        <div className="mt-5 font-serif italic text-white/80 text-[16px] lg:text-[18px] tracking-[-0.005em]">
          Guided by Faith · Built on Character · Committed to Future Leaders
        </div>
      </div>
    </section>
  </main>
);

export default Apply;
